import React, { useEffect, useRef, useState } from "react";
import Logo from "../assets/Brand/cnt-academy-logo-icon-transparent.png";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";
import { NavbarLinks } from "../data/navbar-links";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineShoppingCart } from "react-icons/ai";
import ProfileDropdown from "../components/core/Auth/ProfileDropdown";
import { apiConnector } from "../services/apiconnector";
import { categories } from "../services/apis";
import { SlArrowDown } from "react-icons/sl";
import Spinner from "./Spinner";
import { AiOutlineMenu } from "react-icons/ai";
import { HiOutlineHome, HiUserGroup } from "react-icons/hi";
import { IoIosArrowBack, IoIosCall } from "react-icons/io";
import { IoLogInOutline } from "react-icons/io5";
import { ImUserPlus } from "react-icons/im";
import useOnClickOutside from "../hooks/useOnClickOutside";
import { ACCOUNT_TYPE } from "../utils/constants";
import { getEntireCart } from "../services/operations/cartAPI";
import { setTotalItems } from "../slices/cartSlice";
import { LuBrain } from "react-icons/lu";
import { FaChartLine } from "react-icons/fa";
import { FaSearch } from "react-icons/fa";
import { normalizeApiArray } from "../utils/normalizeApiArray";

const Navbar = () => {
  const { token } = useSelector((state) => state.auth);
  const { user } = useSelector((state) => state.profile);
  const { totalItems } = useSelector((state) => state.cart);

  const [subLinks, setSubLinks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    navigate(`/courses?search=${encodeURIComponent(query)}`);
    setSearchOpen(false);
    setIsNavbarCollapsed(true);
  };

  const ref = useRef(null);

  // Using onClickOutside to handle state changes for clicking outside
  useOnClickOutside(ref, () => setIsNavbarCollapsed(true));

  // eslint-disable-next-line
  const [cart, setCart] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (token && user?.accountType !== ACCOUNT_TYPE.ADMIN) {
      const getCartDetails = async () => {
        setLoading(true);
        try {
          const cartData = await getEntireCart(token);
          setCart(cartData);
          dispatch(setTotalItems(cartData?.data?.data?.totalItems));
        } catch (error) {
          console.log(error);
        }
        setLoading(false);
      };

      getCartDetails();
    }
    // eslint-disable-next-line
  }, [user, token]);

  const fetchSublinks = async () => {
    setLoading(true);
    try {
      const result = await apiConnector("GET", categories.CATEGORIES_API);
      setSubLinks(normalizeApiArray(result?.data?.allCategories));
    } catch (error) {
      console.log("could not fetch the category list", error);
      setSubLinks([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSublinks();
  }, []);

  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const matchRoute = (route) => {
    return matchPath({ path: route }, location.pathname);
  };

  return (
    <>
    <div className={`site-nav-stack ${isDashboard ? "dashboard-nav" : ""}`}>
    <header className="site-header flex h-[72px] items-center justify-center">
      <div
        className={`flex w-11/12 max-w-maxContent justify-between items-center ${
          isNavbarCollapsed ? "" : "relative"
        }`}
      >
        {/* Logo */}
        <Link to={"/"} className="site-brand" aria-label="CNT Academy home">
          <img className="site-logo" src={Logo} alt="" />
          <span className="site-brand-name"><b>Chart <em>N</em> Trade</b><small>Academy</small></span>
        </Link>

        <form className="nav-search hidden md:flex" role="search" onSubmit={submitSearch}>
          <FaSearch aria-hidden="true" />
          <input value={searchQuery} onChange={(event)=>setSearchQuery(event.target.value)} placeholder="Search courses or skills" aria-label="Search courses" />
          <button type="submit">Search</button>
        </form>

        {/* Navlinks for medium and large screens */}
        <nav className="hidden lg:block">
          <ul className="flex items-center gap-x-8 text-[15px] font-medium text-slate-700">
            {NavbarLinks.map((ele, i) => {
              return (
                <li key={i}>
                  {ele.title === "Courses" ? (
                    <div className="nav-link relative flex items-center gap-2 group py-6">
                      <p className="group-hover:cursor-pointer">{ele?.title}</p>
                      <SlArrowDown className="text-sm group-hover:cursor-pointer" />
                      <div className="invisible absolute left-[50%] top-[50%] z-[1000] flex w-[200px] translate-x-[-50%] translate-y-[3em] flex-col rounded-lg bg-richblack-5 p-4 text-richblack-900 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-[1.65em] group-hover:opacity-100 lg:w-[300px]">
                        <div className="absolute left-[50%] top-0 -z-10 h-6 w-6 translate-x-[80%] translate-y-[-40%] rotate-45 select-none rounded bg-richblack-5 "></div>
                        {loading ? (
                          <Spinner />
                        ) : subLinks.length ? (
                          <>
                            <Link
                              to="/courses"
                              className="mb-1 rounded-xl bg-blue-5 px-4 py-3 font-semibold text-blue-700"
                            >
                              View all courses
                            </Link>
                            {subLinks.map((subLink, i) => (
                              <Link
                                to={`/course/${subLink.name
                                  .split(" ")
                                  .join("-")
                                  .toLowerCase()}`}
                                key={i}
                                className="rounded-xl bg-transparent px-4 py-3 font-medium hover:bg-blue-5 hover:text-blue-700"
                              >
                                <p>{subLink.name}</p>
                              </Link>
                            ))}
                          </>
                        ) : (
                          <div className="text-center">No Courses Found</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Link to={ele?.path}>
                      <p
                        className={
                          matchRoute(ele?.path)
                            ? "nav-link nav-link-active"
                            : "nav-link"
                        }
                      >
                        {ele?.title}
                      </p>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Login/signup/ dashboard buttons */}
        <div className="flex gap-x-4 items-center">
          <button className="nav-search-toggle md:hidden" aria-label="Open course search" onClick={()=>setSearchOpen(!searchOpen)}><FaSearch /></button>
          {user && user?.accountType !== "Admin" && (
            <Link to={"/dashboard/cart"} className="relative">
              <AiOutlineShoppingCart className="hidden lg:block text-2xl text-richblack-100" />
              {totalItems > 0 && (
                <span className="hidden lg:grid absolute -bottom-2 -right-2 h-5 w-5 place-items-center overflow-hidden rounded-full bg-richblack-600 text-center text-xs font-bold text-yellow-100">
                  {totalItems}
                </span>
              )}
            </Link>
          )}
          {token === null && (
            <Link to={"/login"}>
              <button className="nav-button nav-button-secondary hidden lg:block">
                Log in
              </button>
            </Link>
          )}
          {token === null && (
            <Link to={"/signup"}>
              <button className="nav-button nav-button-primary hidden lg:block">
                Sign up
              </button>
            </Link>
          )}
          {token !== null && <ProfileDropdown />}
          {/* Hamburger for small screens */}
          <button
            className="mobile-menu-button mr-2 lg:hidden"
            onClick={() => setIsNavbarCollapsed(!isNavbarCollapsed)}
          >
            <AiOutlineMenu fontSize={24} fill="#1d4ed8" />
          </button>
        </div>
        {searchOpen && <form className="nav-search nav-search-mobile md:hidden" role="search" onSubmit={submitSearch}><FaSearch/><input autoFocus value={searchQuery} onChange={(event)=>setSearchQuery(event.target.value)} placeholder="Search courses or skills" aria-label="Search courses"/><button type="submit">Search</button></form>}

        {/* Collapsible menu for small screens */}
        <div
          ref={ref}
          className={`lg:hidden ${
            isNavbarCollapsed
              ? "hidden"
              : "mobile-nav block absolute z-50 top-12 right-0"
          }`}
        >
          <div className="absolute -top-1 w-4 h-4 bg-white rotate-45 right-6 translate-x-[45%]"></div>
          <ul className="flex min-w-[260px] justify-center px-4 py-4 flex-col gap-2 text-slate-700 rounded-2xl">
            {NavbarLinks.map((ele, i) => {
              return (
                <li key={i} className="group">
                  {ele.title === "Courses" ? (
                    <div className="relative gap-x-2 flex items-center group mb-2 ">
                      <IoIosArrowBack className="group-hover:cursor-pointer -rotate-90" />
                      <p className="group-hover:cursor-pointer">{ele.title}</p>
                      <div className="invisible absolute left-[30%] top-[10%] z-[1000] flex w-[220px] translate-x-[-70%] -translate-y-[3em] flex-col rounded-lg bg-richblack-5 p-2 text-richblack-900 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-[1.65em] group-hover:opacity-100 lg:w-[300px]">
                        <div className="absolute  right-[52%] top-[1%] -z-10 h-6 w-6 translate-x-[80%] translate-y-[-40%] rotate-45 select-none rounded bg-richblack-5 "></div>
                        {loading ? (
                          <Spinner />
                        ) : subLinks.length ? (
                          <>
                            <Link
                              to="/courses"
                              className="rounded-lg bg-blue-5 py-3 pl-2 font-semibold text-blue-700"
                              onClick={() => setIsNavbarCollapsed(true)}
                            >
                              View all courses
                            </Link>
                            {subLinks.map((subLink, i) => (
                              <Link
                                to={`/course/${subLink.name
                                  .split(" ")
                                  .join("-")
                                  .toLowerCase()}`}
                                key={i}
                                className="rounded-lg bg-transparent py-3 pl-2 hover:bg-richblack-50"
                              >
                                <p onClick={() => setIsNavbarCollapsed(true)}>
                                  {subLink.name}
                                </p>
                              </Link>
                            ))}
                          </>
                        ) : (
                          <div className="text-center">No Courses Found</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Link to={ele.path}>
                      <div
                        className={`flex mb-2 gap-x-2 items-center group-hover:text-yellow-50`}
                      >
                        <span>
                          {ele.title === "Home" && <HiOutlineHome />}
                          {ele.title === "Courses" && <IoIosArrowBack />}
                          {ele.title === "About Us" && <HiUserGroup />}
                          {ele.title === "Contact Us" && <IoIosCall />}
                          {ele.title === "Knowledge Center" && <LuBrain />}
                          {ele.title === "Plans" && <FaChartLine />}
                        </span>
                        <p onClick={() => setIsNavbarCollapsed(true)}>
                          {ele.title}
                        </p>
                      </div>
                    </Link>
                  )}
                </li>
              );
            })}
            <div className="mt-1 flex flex-col border-t border-t-richblack-500">
              {token === null && (
                <Link to={"/login"}>
                  <button
                    onClick={() => setIsNavbarCollapsed(true)}
                    className="border w-full flex gap-x-2 items-center border-richblack-700 bg-richblack-800 px-[12px] py-[8px] mt-2 text-richblack-100 rounded-md"
                  >
                    <IoLogInOutline />
                    Log in
                  </button>
                </Link>
              )}
              {token === null && (
                <Link to={"/signup"}>
                  <button
                    onClick={() => setIsNavbarCollapsed(true)}
                    className="border w-full flex gap-x-2 items-center border-richblack-700 bg-richblack-800 px-[12px] py-[8px] text-richblack-100 rounded-md"
                  >
                    <ImUserPlus />
                    Sign up
                  </button>
                </Link>
              )}
            </div>
          </ul>
        </div>
      </div>
    </header>
    {!isDashboard && <nav className="market-nav" aria-label="Learning sections">
      <Link to="/courses">Courses <span>▣</span></Link>
      <Link to="/programs">Programs <span>▣</span></Link>
      <Link to="/mentorship">Mentorship <span>♙</span></Link>
    </nav>}
    </div>
    <div className={`site-nav-spacer ${isDashboard ? "dashboard-nav" : ""}`} aria-hidden="true" />
    </>
  );
};

export default Navbar;
